/* here&found — <hf-carousel> (assets/hf-carousel.js).
   Auto-advancing carousel (reviews, "The mechanism" product stories).
   Auto-advance every 5s (override with an `interval` attribute, ms), pause on
   hover and on keyboard focus, prev/next buttons, and the interval is cleared
   on disconnect — always.

   Contract with sections:
   - slides are [data-slide]; the section's CSS shows ONLY the first slide by
     default (the no-JS state: a static first slide) and shows .is-active once
     the element has [data-ready];
   - prev/next buttons are [data-carousel-prev] / [data-carousel-next],
     shipped `hidden` so they never render when JS is off;
   - optional progress dots are [data-carousel-dot] (one per slide, in slide
     order) — kept in sync via .is-active + aria-current, click jumps to the
     slide. Sections hide the dot pill until [data-ready] for the no-JS state.

   prefers-reduced-motion → no auto-advance; prev/next still work.
   Also pauses when the tab is hidden or the carousel leaves the viewport. */

const HF_CAROUSEL_INTERVAL = 5000;

class HFCarousel extends HTMLElement {
  connectedCallback() {
    this.slides = Array.from(this.querySelectorAll('[data-slide]'));
    if (this.slides.length < 2) return;
    this.index = Math.max(0, this.slides.findIndex((s) => s.classList.contains('is-active')));
    this.paused = false;
    this.prev = this.querySelector('[data-carousel-prev]');
    this.next = this.querySelector('[data-carousel-next]');
    this.dots = Array.from(this.querySelectorAll('[data-carousel-dot]'));
    this.onDot = (e) => {
      const i = this.dots.indexOf(e.currentTarget);
      if (i >= 0) this.go(i, true);
    };
    this.onPrev = () => this.go(this.index - 1, true);
    this.onNext = () => this.go(this.index + 1, true);
    this.onEnter = () => this.setPaused(true);
    this.onLeave = () => this.setPaused(false);
    this.onVisibility = () => this.sync();
    // Theme editor: selecting a slide block shows that slide (Shopify fires
    // these only inside the customizer — inert on the storefront).
    this.onBlockSelect = (e) => {
      const i = this.slides.findIndex((s) => s === e.target || s.contains(e.target) || e.target.contains(s));
      if (i >= 0) {
        this.setPaused(true);
        this.go(i, true);
      }
    };
    this.onBlockDeselect = () => this.setPaused(false);

    if (this.prev) {
      this.prev.removeAttribute('hidden');
      this.prev.addEventListener('click', this.onPrev);
    }
    if (this.next) {
      this.next.removeAttribute('hidden');
      this.next.addEventListener('click', this.onNext);
    }
    this.dots.forEach((dot) => dot.addEventListener('click', this.onDot));
    this.addEventListener('mouseenter', this.onEnter);
    this.addEventListener('mouseleave', this.onLeave);
    this.addEventListener('focusin', this.onEnter);
    this.addEventListener('focusout', this.onLeave);
    document.addEventListener('visibilitychange', this.onVisibility);
    document.addEventListener('shopify:block:select', this.onBlockSelect);
    document.addEventListener('shopify:block:deselect', this.onBlockDeselect);

    this.io = new IntersectionObserver((entries) => {
      this.offscreen = !entries[0].isIntersecting;
      this.sync();
    });
    this.io.observe(this);

    this.setAttribute('data-ready', '');
    this.render();
    this.sync();
  }

  disconnectedCallback() {
    this.stop();
    if (this.prev) this.prev.removeEventListener('click', this.onPrev);
    if (this.next) this.next.removeEventListener('click', this.onNext);
    if (this.dots) this.dots.forEach((dot) => dot.removeEventListener('click', this.onDot));
    this.removeEventListener('mouseenter', this.onEnter);
    this.removeEventListener('mouseleave', this.onLeave);
    this.removeEventListener('focusin', this.onEnter);
    this.removeEventListener('focusout', this.onLeave);
    document.removeEventListener('visibilitychange', this.onVisibility);
    document.removeEventListener('shopify:block:select', this.onBlockSelect);
    document.removeEventListener('shopify:block:deselect', this.onBlockDeselect);
    if (this.io) this.io.disconnect();
  }

  setPaused(paused) {
    this.paused = paused;
    this.sync();
  }

  sync() {
    const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    const shouldRun = !reduced && !this.paused && !this.offscreen && !document.hidden;
    if (shouldRun) this.start();
    else this.stop();
  }

  start() {
    if (this.timer) return;
    const interval = parseInt(this.getAttribute('interval'), 10) || HF_CAROUSEL_INTERVAL;
    this.timer = setInterval(() => this.go(this.index + 1, false), interval);
  }

  stop() {
    if (this.timer) clearInterval(this.timer);
    this.timer = null;
  }

  go(index, manual) {
    this.index = (index + this.slides.length) % this.slides.length;
    this.render();
    if (manual) {
      // restart the clock so a manual step isn't immediately auto-advanced past
      this.stop();
      this.sync();
    }
  }

  render() {
    this.slides.forEach((slide, i) => {
      slide.classList.toggle('is-active', i === this.index);
      slide.toggleAttribute('aria-hidden', i !== this.index);
    });
    this.dots.forEach((dot, i) => {
      dot.classList.toggle('is-active', i === this.index);
      if (i === this.index) dot.setAttribute('aria-current', 'true');
      else dot.removeAttribute('aria-current');
    });
  }
}

if (!customElements.get('hf-carousel')) customElements.define('hf-carousel', HFCarousel);
